
/**
 * @mixin
 * @description Mixin auxiliar da aba Roadmap. Mantém todos os entregáveis (discovery + roadmaps) em um único array `roadmapRows`.
 * @requires {@link BusinessPlanCore} requer o mixin base para o `businessPlanId` e o `clientId`.
 * @example
 * mixins: [BusinessPlanParticipants]
 */
export default {
  data() {
    return {
      /** @type {import('../../businessplan.types').Platform[]} */
      platforms: [],
      /** @type {import('../../businessplan.types').Roadmap[]} */
      roadmapRows: []
    };
  },
  computed: {
    /**
     * Indica se já existe ao menos um Discovery cadastrado (entregavel === 0)
     * @type {boolean}
     */
    hasDiscovery() {
      return Array.isArray(this.roadmapRows) &&
        this.roadmapRows.some(({ entregavel }) => entregavel === 0);
    },
    /**
     * Integração com API para salvar e recuperar os dados dos entregaveis (i.e. roadmaps) de um Business Plan
     * @type {Object}
     * @readonly
     */
    roadmapResource() {
      return this.apiResource(
        `/v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/roadmap`
      );
    }
  },
  methods: {
    /**
     * Recupera da API a lista de plataformas exigido no cadastro da aba Roadmap.
     * Processa os dados para garantir uma ordenação alfabética crescente.
     */
    async getPlatforms() {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/plataformas/selecao`
        );
        const response = await resource.get();
        this.platforms = response.sort(({ plataforma: a }, { plataforma: b }) =>
          a.localeCompare(b)
        );
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
        this.platforms = [];
      }
    },
    /**
     * Carrega todos os entregáveis (roadmaps + discovery) da API em um único array.
     * @param {boolean} skip determina se pula ou não a atualização das datas inicial e final do BP
     * @returns {Promise}
     */
    async doLoadRoadmap(skip = false) {
      if (!this.businessPlanId) {
        return;
      }

      try {
        this.roadmapRows = await this.roadmapResource.get();

        if (skip) {
          return;
        }

        // Alterações de roadmaps podem alterar as datas inicial e final do BP
        const businnesPlan = await this.resource.get(this.businessPlanId);
        this.formData.data_inicio = businnesPlan.data.data_inicio;
        this.formData.data_fim = businnesPlan.data.data_fim;
      } catch (error) {
        this.roadmapRows = [];
      }
    },
    /**
     * Cadastra ou atualiza um entregavel (i.e. roadmap) de um BP.
     * Ao final da chamada, chama `doLoadRoadmap` para garantir que ambas as abas continuem atualizadas.
     * @param {number} tab identificação da aba que está cadastrando ou atualizando (possuem validações diferentes)
     * @param {Object} modalEvent evento do componente FormModal
     * @param {Object} modalEvent.data dados do formulário para cadastro/atualização.
     * @param {Object} modalEvent.close função para fechar a modal programaticamente.
     */
    async doSaveRoadmap(tab, { data, close }) {
      if (!this.businessPlanId) {
        return;
      }

      await this.roadmapResource.save(data, data.id);

      this.$notify({
        group: "geral",
        duration: 5000,
        type: "sucess",
        title: `${data.id ? "Edição" : "Inclusão"} do entregável concluída`,
        text: `O entregável foi ${data.id ? "alterado" : "criado"} com sucesso.`
      });

      if (close) {
        close();
      }

      await this.doLoadRoadmap();
      this.$refs.alocacao.doReload();
    },
    /**
     * Atualiza o status de um ou mais entregáveis via rota dedicada (apenas versão oficial).
     * @param {number[]} ids lista de IDs dos entregáveis a atualizar
     * @param {string} status novo status a aplicar
     * @param {Function} close função para fechar o modal de edição de status
     */
    async doSaveRoadmapStatus({ ids, status, close }) {
      if (!this.businessPlanId) {
        return;
      }

      try {
        await this.roadmapResource.patch({ ids, status }, "status");

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Status atualizado",
          text: `Status dos entregáveis atualizado para "${status}" com sucesso.`
        });

        if (close) {
          close();
        }

        await this.doLoadRoadmap({ skip: true });
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao atualizar Status",
          text: "Não foi possível atualizar o Status. Tente novamente."
        });
      }
    },
    /**
     * Exclusão de um entregavel (i.e. roadmap).
     * Ao final da chamada, chama `doLoadRoadmap` para garantir que ambas as abas continuem atualizadas.
     * @param {number} roadmapId identificador único do registro.
     */
    async doDeleteRoadmap(roadmapId) {
      if (!this.businessPlanId) {
        return;
      }

      const confirm = await this.$refs.confirmRoadmapDelete.asyncOpen();

      if (!confirm) {
        return;
      }

      await this.roadmapResource.delete(roadmapId);

      this.$notify({
        group: "geral",
        duration: 5000,
        type: "sucess",
        title: "Exclusão do roadmap concluída",
        text: "Roadmap apagado com sucesso."
      });

      await this.doLoadRoadmap();
      this.$refs.alocacao.doReload();
    }
  }
};
